import type * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { renderPromise } from '../util/PromiseRenderer'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { GetFileEndpoint } from '../../../server/client/client.no-deps'

export type ThumbnailUrls = { [key: FileId]: string }
type RenderWithS3Url = (response: { thumbnailS3Url?: string; fullSizeS3Url?: string }) => React.ReactElement<unknown>

/**
 * React helper to fetch the s3 url for a file so that it can be used in <img> or <video>
 */
export function withS3Url(deps: CommonDeps, fetchUrl: string, render: RenderWithS3Url) {
    return renderPromise(deps, fetchUrl, () => fetchS3UrlBothQuality(deps, fetchUrl), render)
}

/**
 * Same as withS3Url but with an fallback image if the url could not be found
 */
export function withS3ImageUrl(deps: CommonDeps, fetchUrl: string, render: RenderWithS3Url) {
    return withS3Url(deps, fetchUrl, ({ thumbnailS3Url, fullSizeS3Url }) =>
        render({ thumbnailS3Url: thumbnailS3Url ?? deps.getImage('zaptic-fallback-icon'), fullSizeS3Url })
    )
}

/**
 * In order to authenticate files with a bearer token, we need to request the
 * server gives us a temporary url for the file that we can use directly.
 * This is because we cannot use <img> or <video> with a bearer token directly
 */
export async function fetchS3UrlBothQuality(
    deps: CommonDeps,
    fetchUrl: string
): Promise<{ thumbnailS3Url?: string; fullSizeS3Url?: string }> {
    try {
        if (fetchUrl == null) {
            return { thumbnailS3Url: null, fullSizeS3Url: null }
        }

        // Extract values from the url
        const url = new URL(fetchUrl)
        const [, api, files, fileId] = url.pathname.split('/')

        // If the url doesn't look as expected, return nothing
        if (api !== 'api' || files !== 'files') {
            return { thumbnailS3Url: null, fullSizeS3Url: null }
        }

        // Get the temporary url for AWS
        const [thumbnailS3Url, fullSizeS3Url] = await Promise.all(
            [
                deps.httpClient.getFile(fileId as FileId, { thumbnail: true, doNotRedirect: true }),
                deps.httpClient.getFile(fileId as FileId, { thumbnail: false, doNotRedirect: true }),
            ].map(getFileResponseToResult)
        )
        //thumbnail might not exist
        return { thumbnailS3Url: thumbnailS3Url ?? fullSizeS3Url, fullSizeS3Url }
    } catch (error) {
        deps.reportError(error)
        return { thumbnailS3Url: null, fullSizeS3Url: null }
    }
}

function getFileResponseToResult(call: Promise<GetFileEndpoint['Output']>): Promise<string | null> {
    return call
        .catch(() => {
            /* in case thumbnail missing*/ return null
        })
        .then((body) => (body ? (typeof body === 'string' ? body : body.downloadUrl) : null))
}

/**
 * In order to authenticate files with a bearer token, we need to request the
 * server gives us a temporary url for the file that we can use directly.
 * This is because we cannot use <img> or <video> with a bearer token directly
 */
export function fetchS3Urls(deps: CommonDeps, fileIds: FileId[], thumbnail: boolean): Promise<ThumbnailUrls> {
    if (fileIds.length === 0) {
        return Promise.resolve({})
    }
    return deps.httpClient.getFilesS3Url(fileIds, { thumbnail }).then((output) => output.fileUrls)
}

/**
 * Too much of the infrastructure depends on image ids being a fetch url, even though a FileId is all we need
 * So this puts the the FileId back into a fake url.
 */
export function fakeFetchUrl(fileId: FileId | null, thumbnail?: boolean) {
    if (fileId == null) return null
    return location.origin + `/api/files/${fileId}?thumbnail=${thumbnail ? 'true' : 'false'}`
}
